import api from './utils/axios';
import { TagSpecAllowedValue } from '@/types/tag';

export interface TenantConfiguration {
  id?: number;
  tenant?: number;
  agent_first_message: string;
  dashboard_config: {
    tabs: DashboardTab[];
    promotion_bar_title?: string | null;
  };
  created_at?: string;
  updated_at?: string;
}

export interface DashboardTab {
  id: string;
  label: string;
  tag: string;
  priority: number;
  is_active: boolean;
}

export interface TagSpec {
  id?: number;
  name: string;
  description: string;
  constraints: {
    allowed_values: TagSpecAllowedValue[];
  };
  is_active: boolean;
  created_at?: string;
}

export interface Tag {
  id?: number;
  tag_spec_id: number;
  value: string;
  metadata?: {
    icon?: string;
    color?: string;
  };
  created_at?: string;
}

export class TenantConfigService {
  private readonly TENANT_CONFIG_PREFIX = 'org-administration/tenant-config';
  private readonly TAG_SPECS_PREFIX = 'tag-specs';
  private readonly TAGS_PREFIX = 'tags';
  private readonly TENANT_PREFIX = 'org-administration/tenants/';

  /**
   * Get tenant configuration by config ID
   */
  async getTenantConfig(tenantid: number): Promise<TenantConfiguration> {
    try {
      // Name the tenant explicitly: the URL-derived header can be stale or
      // missing if the page navigates while the request is queued.
      const response = await api.get(
        `${this.TENANT_PREFIX}${tenantid}/config/`,
        { headers: { 'x-tenant-id': tenantid.toString() } }
      );
      return response.data;
    } catch (error) {
      // 404 just means this tenant has no config; callers fall back to defaults.
      const status = (error as { response?: { status?: number } })?.response
        ?.status;
      if (status !== 404) {
        console.error('Error fetching tenant config:', error);
      }
      throw error;
    }
  }

  /**
   * Create tenant configuration
   */
  async createTenantConfig(
    data: Omit<TenantConfiguration, 'id' | 'created_at' | 'updated_at'>
  ): Promise<TenantConfiguration> {
    try {
      const response = await api.post(`${this.TENANT_CONFIG_PREFIX}/`, data);
      return response.data;
    } catch (error) {
      console.error('Error creating tenant config:', error);
      throw error;
    }
  }

  /**
   * Update tenant configuration (full)
   */
  async updateTenantConfig(
    configId: number,
    data: Omit<
      TenantConfiguration,
      'id' | 'tenant' | 'created_at' | 'updated_at'
    >
  ): Promise<TenantConfiguration> {
    try {
      const response = await api.put(
        `${this.TENANT_CONFIG_PREFIX}/${configId}/`,
        data
      );
      return response.data;
    } catch (error) {
      console.error('Error updating tenant config:', error);
      throw error;
    }
  }

  /**
   * Update tenant configuration (partial)
   */
  async patchTenantConfig(
    configId: number,
    data: Partial<
      Omit<TenantConfiguration, 'id' | 'tenant' | 'created_at' | 'updated_at'>
    >
  ): Promise<TenantConfiguration> {
    try {
      const response = await api.patch(
        `${this.TENANT_CONFIG_PREFIX}/${configId}/`,
        data
      );
      return response.data;
    } catch (error) {
      console.error('Error patching tenant config:', error);
      throw error;
    }
  }

  /**
   * Get all tag specifications
   */
  async getTagSpecs(): Promise<{ count: number; results: TagSpec[] }> {
    try {
      const response = await api.get(`${this.TAG_SPECS_PREFIX}/`);
      return response.data;
    } catch (error) {
      console.error('Error fetching tag specs:', error);
      throw error;
    }
  }

  /**
   * Create tag specification
   */
  async createTagSpec(
    data: Omit<TagSpec, 'id' | 'created_at'>
  ): Promise<TagSpec> {
    try {
      const response = await api.post(`${this.TAG_SPECS_PREFIX}/`, data);
      return response.data;
    } catch (error) {
      console.error('Error creating tag spec:', error);
      throw error;
    }
  }

  /**
   * Update tag specification
   */
  async updateTagSpec(id: number, data: Partial<TagSpec>): Promise<TagSpec> {
    try {
      const response = await api.patch(`${this.TAG_SPECS_PREFIX}/${id}/`, data);
      return response.data;
    } catch (error) {
      console.error('Error updating tag spec:', error);
      throw error;
    }
  }

  /**
   * Get all tags
   */
  async getTags(): Promise<{ count: number; results: Tag[] }> {
    try {
      const response = await api.get(`${this.TAGS_PREFIX}/`);
      return response.data;
    } catch (error) {
      console.error('Error fetching tags:', error);
      throw error;
    }
  }

  /**
   * Create tag
   */
  async createTag(data: Omit<Tag, 'id' | 'created_at'>): Promise<Tag> {
    try {
      const response = await api.post(`${this.TAGS_PREFIX}/`, data);
      return response.data;
    } catch (error) {
      console.error('Error creating tag:', error);
      throw error;
    }
  }

  /**
   * Update tag
   */
  async updateTag(id: number, data: Partial<Tag>): Promise<Tag> {
    try {
      const response = await api.patch(`${this.TAGS_PREFIX}/${id}/`, data);
      return response.data;
    } catch (error) {
      console.error('Error updating tag:', error);
      throw error;
    }
  }

  /**
   * Delete tag
   */
  async deleteTag(id: number): Promise<void> {
    try {
      await api.delete(`${this.TAGS_PREFIX}/${id}/`);
    } catch (error) {
      console.error('Error deleting tag:', error);
      throw error;
    }
  }
}

export default new TenantConfigService();
